import { Link } from 'react-router-dom'
import { useTranslation } from '../i18n'
import type { EvolutionResponse } from '../types'

interface RecentBuildsProps {
  data: EvolutionResponse | null
  loading: boolean
}

interface FailureCountProps {
  count: number
  buildId: string
  linkLabel: string
}

function FailureCount({ count, buildId, linkLabel }: FailureCountProps) {
  if (count === 0) {
    return <>{count}</>
  }
  return (
    <Link to={`/failing?buildId=${encodeURIComponent(buildId)}`} title={linkLabel}>
      {count}
    </Link>
  )
}

export function RecentBuilds({ data, loading }: RecentBuildsProps) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="card" style={{ marginTop: '1.5rem' }} data-testid="recent-builds">
        <h2 className="card-title">{t('component.recentBuilds')}</h2>
        <div
          style={{
            color: 'var(--color-gray-400)',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          {t('label.loading')}
        </div>
      </div>
    )
  }

  if (!data || data.builds.length === 0) {
    return (
      <div className="card" style={{ marginTop: '1.5rem' }} data-testid="recent-builds">
        <h2 className="card-title">{t('component.recentBuilds')}</h2>
        <div
          style={{
            color: 'var(--color-gray-400)',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          {t('empty.noBuilds')}
        </div>
      </div>
    )
  }

  const recentBuilds = [...data.builds].reverse().slice(0, 10)

  return (
    <div className="card" style={{ marginTop: '1.5rem' }} data-testid="recent-builds">
      <h2 className="card-title">{t('component.recentBuilds')}</h2>
      <table className="table">
        <thead>
          <tr>
            <th>{t('label.buildId')}</th>
            <th>{t('label.date')}</th>
            <th>{t('label.total')}</th>
            <th>{t('label.passed')}</th>
            <th>{t('label.failed')}</th>
            <th>{t('label.error')}</th>
            <th>{t('label.skipped')}</th>
            <th>{t('label.passRate')}</th>
          </tr>
        </thead>
        <tbody>
          {recentBuilds.map((build) => (
            <tr key={build.buildId}>
              <td>
                <code>{build.buildId}</code>
              </td>
              <td>{new Date(build.date).toLocaleString()}</td>
              <td>{build.total}</td>
              <td className="stat-success">{build.passed}</td>
              <td
                className={build.failed > 0 ? 'stat-error' : ''}
                data-testid={`build-failed-${build.buildId}`}
              >
                <FailureCount
                  count={build.failed}
                  buildId={build.buildId}
                  linkLabel={t('label.showFailingTests')}
                />
              </td>
              <td
                className={build.error > 0 ? 'stat-error' : ''}
                data-testid={`build-errors-${build.buildId}`}
              >
                <FailureCount
                  count={build.error}
                  buildId={build.buildId}
                  linkLabel={t('label.showFailingTests')}
                />
              </td>
              <td className={build.skipped > 0 ? 'stat-warning' : ''}>{build.skipped}</td>
              <td>
                <span className={build.passRate >= 80 ? 'stat-success' : 'stat-warning'}>
                  {build.passRate.toFixed(1)}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
